import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Instagram, Facebook, Linkedin, FileText, MessageSquare, Zap, Loader2,
  Share2, Send, Heart, Sparkles, RefreshCw
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SocialMediaManagerPage = () => {
  const [selectedChannel, setSelectedChannel] = useState('instagram');
  const [channels, setChannels] = useState({});
  const [generatedContent, setGeneratedContent] = useState(null);
  const [recentContent, setRecentContent] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [useRiona, setUseRiona] = useState(false);
  const [rionaEnabled, setRionaEnabled] = useState(false);
  const [rionaStatus, setRionaStatus] = useState(null);
  const [rionaLogs, setRionaLogs] = useState([]);
  const [rionaLiveMode, setRionaLiveMode] = useState(false);
  const [switchingMode, setSwitchingMode] = useState(false);
  const [customTopic, setCustomTopic] = useState('');
  const [rionaTargetUrl, setRionaTargetUrl] = useState('');

  const channelIcons = {
    instagram: Instagram,
    facebook: Facebook,
    tiktok: () => <span className="font-bold">TT</span>,
    linkedin: Linkedin,
    blog: FileText,
    whatsapp: MessageSquare,
    moltbook: Zap
  };

  const channelColors = {
    instagram: 'from-pink-500 to-purple-500',
    facebook: 'from-blue-500 to-blue-600',
    tiktok: 'from-gray-900 to-gray-700',
    linkedin: 'from-blue-600 to-blue-700',
    blog: 'from-green-500 to-emerald-600',
    whatsapp: 'from-green-400 to-green-500',
    moltbook: 'from-fox-fire to-amber-500'
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [channelsRes, contentRes, rionaRes, logsRes] = await Promise.all([
        axios.get(`${API}/multichannel/channels`),
        axios.get(`${API}/multichannel/content?limit=10`),
        axios.get(`${API}/riona/config`),
        axios.get(`${API}/riona/logs?limit=10`)
      ]);
      setChannels(channelsRes.data.channels || {});
      setRionaEnabled(channelsRes.data.riona_enabled);
      setRecentContent(contentRes.data.content || []);
      setRionaStatus(rionaRes.data);
      setRionaLiveMode(rionaRes.data?.mode === 'live');
      setRionaLogs(logsRes.data?.logs || []);
    } catch (error) {
      { /* Fetch fehlgeschlagen – kein kritischer Fehler */ }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSwitchRionaMode = async (newMode) => {
    setSwitchingMode(true);
    try {
      const response = await axios.post(`${API}/riona/mode`, { mode: newMode });
      setRionaLiveMode(newMode === 'live');
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error('Fehler beim Moduswechsel: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSwitchingMode(false);
    }
  };

  const handleGenerateContent = async () => {
    setGenerating(true);
    try {
      const response = await axios.post(`${API}/multichannel/generate`, {
        channel: selectedChannel,
        topic: customTopic || null,
        use_riona: useRiona,
        auto_post: false
      });
      setGeneratedContent(response.data.content);
      toast.success(`${channels[selectedChannel]?.name || selectedChannel} Content generiert!`);
      fetchData();
    } catch (error) {
      toast.error('Fehler: ' + (error.response?.data?.detail || error.message));
    } finally {
      setGenerating(false);
    }
  };

  const handleRionaPost = async () => {
    if (!generatedContent?.id) return;
    try {
      const response = await axios.post(`${API}/riona/instagram/post`, { post_id: generatedContent.id });
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error('Riona Fehler: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleRionaLike = async () => {
    if (!rionaTargetUrl) { toast.error('Bitte eine Instagram-Post-URL eingeben'); return; }
    try {
      const response = await axios.post(`${API}/riona/instagram/like`, { target_url: rionaTargetUrl });
      toast.success(response.data.message);
    } catch (error) {
      toast.error('Riona Fehler: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleRionaComment = async () => {
    if (!rionaTargetUrl) { toast.error('Bitte eine Instagram-Post-URL eingeben'); return; }
    try {
      const response = await axios.post(`${API}/riona/instagram/comment`, { target_url: rionaTargetUrl });
      toast.success(response.data.message);
    } catch (error) {
      toast.error('Riona Fehler: ' + (error.response?.data?.detail || error.message));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-fox-fire" />
      </div>
    );
  }

  return (
    <div data-testid="social-media-page" className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-stone-900 flex items-center gap-3">
            <Share2 className="w-8 h-8 text-fox-fire" />
            Multi-Channel Social Media Manager
          </h1>
          <p className="text-stone-500 mt-1">Content für alle Kanäle generieren und posten</p>
        </div>
      </div>

      {/* Channel Selection */}
      <div className="bg-white rounded-xl border border-stone-200 p-6">
        <h2 className="font-heading text-lg font-bold mb-4">Kanal wählen</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          {Object.entries(channels).map(([key, config]) => {
            const Icon = channelIcons[key] || FileText;
            const isSelected = selectedChannel === key;
            return (
              <button
                key={key}
                onClick={() => setSelectedChannel(key)}
                className={`p-4 rounded-xl border-2 transition-all ${
                  isSelected
                    ? `border-transparent bg-gradient-to-br ${channelColors[key]} text-white shadow-lg`
                    : 'border-stone-200 hover:border-stone-300 bg-white'
                }`}
              >
                <div className="flex flex-col items-center gap-2">
                  <Icon size={24} />
                  <span className="text-sm font-medium">{config.name}</span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Generation Panel */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Input */}
        <div className="bg-white rounded-xl border border-stone-200 p-6 space-y-4">
          <h2 className="font-heading text-lg font-bold">Content generieren</h2>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-2">Thema (optional)</label>
            <input
              type="text"
              value={customTopic}
              onChange={(e) => setCustomTopic(e.target.value)}
              placeholder="z.B. Bewerbungscoaching, AVGS-Förderung..."
              className="w-full p-3 border border-stone-200 rounded-lg focus:border-fox-fire focus:ring-2 focus:ring-fox-fire/20"
            />
          </div>

          {/* Riona Toggle */}
          {selectedChannel === 'instagram' && (
            <div className="space-y-4">
              <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                      <span className="text-xl">🤖</span>
                    </div>
                    <div>
                      <h3 className="font-medium text-purple-900">Riona AI Agent</h3>
                      <p className="text-sm text-purple-600">
                        {rionaEnabled ? 'Content-Planung & Logging' : 'Deaktiviert'}
                      </p>
                    </div>
                  </div>
                  <button
                    data-testid="riona-toggle-btn"
                    onClick={() => setUseRiona(!useRiona)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      useRiona
                        ? 'bg-purple-500 text-white'
                        : 'bg-white text-purple-600 border border-purple-300'
                    }`}
                  >
                    {useRiona ? 'Aktiv' : 'Aus'}
                  </button>
                </div>
              </div>

              {useRiona && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <span className="text-amber-600">⚠️</span>
                    <div className="text-sm">
                      <p className="font-medium text-amber-800">Browser-Automation deaktiviert</p>
                      <p className="text-amber-700">
                        Instagram erkennt und blockiert automatisierte Logins.
                        Für echtes Posting wird die <strong>Meta Graph API</strong> empfohlen (Business/Creator Account).
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {useRiona && (
                <div className="p-4 rounded-lg border-2 bg-blue-50 border-blue-200">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h4 className="font-medium text-stone-900">Aktueller Modus</h4>
                      <p className="text-sm text-blue-700">
                        🟢 <strong>Simulation / Logging only</strong> - Kein echtes Instagram-Posting
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <button
                      data-testid="riona-simulation-btn"
                      onClick={() => handleSwitchRionaMode('simulation')}
                      disabled={switchingMode || !rionaLiveMode}
                      className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${
                        !rionaLiveMode
                          ? 'bg-blue-500 text-white shadow-lg'
                          : 'bg-white text-stone-700 border border-stone-300 hover:border-blue-400'
                      }`}
                    >
                      {switchingMode && !rionaLiveMode ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                      🟢 Simulation
                      <span className="text-xs opacity-75">(aktiv)</span>
                    </button>
                    <button
                      data-testid="riona-live-btn"
                      onClick={() => handleSwitchRionaMode('live')}
                      disabled={switchingMode || rionaLiveMode}
                      className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${
                        rionaLiveMode
                          ? 'bg-orange-500 text-white shadow-lg'
                          : 'bg-white text-stone-700 border border-stone-300 hover:border-orange-400'
                      }`}
                    >
                      {switchingMode && rionaLiveMode ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                      📝 Live-Logging
                      <span className="text-xs opacity-75">(nur Log)</span>
                    </button>
                  </div>

                  <div className="mt-3 p-3 bg-green-50 rounded-lg border border-green-200">
                    <p className="text-sm text-green-800">
                      <strong>🚀 Empfehlung für echtes Posting:</strong><br />
                      Meta Graph API einrichten (Business/Creator Account + Facebook-Seite).<br />
                      <span className="text-xs text-green-600">Status: Setup ausstehend • <a href="https://developers.facebook.com/docs/instagram-api" target="_blank" rel="noopener noreferrer" className="underline">Anleitung →</a></span>
                    </p>
                  </div>

                  {rionaLiveMode && (
                    <div className="mt-3 p-3 bg-blue-100 rounded-lg text-sm text-blue-800">
                      <strong>📋 Logging-Modus aktiv:</strong>
                      <p className="mt-1">Alle Aktionen werden nur geloggt, nicht ausgeführt.</p>
                      <p className="text-xs mt-2 text-blue-600">
                        Limits (für spätere Meta API): Max. 2 Posts/Tag, 30 Likes/Std, 10 Kommentare/Tag, 08-20 Uhr
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <button
            onClick={handleGenerateContent}
            disabled={generating}
            className="w-full btn-primary flex items-center justify-center gap-2"
          >
            {generating ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Generiere...</>
            ) : (
              <><Sparkles size={18} /> {channels[selectedChannel]?.name} Content generieren</>
            )}
          </button>

          {selectedChannel === 'instagram' && useRiona && (
            <div className="space-y-2">
              <input
                type="url"
                value={rionaTargetUrl}
                onChange={e => setRionaTargetUrl(e.target.value)}
                placeholder="Instagram-Post-URL für Like/Comment (z.B. https://instagram.com/p/...)"
                className="w-full px-3 py-2 border border-stone-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-fox-fire/30"
              />
              <div className="flex gap-2">
                <button
                  onClick={handleRionaPost}
                  disabled={!generatedContent}
                  className="flex-1 btn-secondary flex items-center justify-center gap-2 text-sm"
                >
                  <Send size={14} /> Riona Post
                </button>
                <button
                  onClick={handleRionaLike}
                  className="flex-1 btn-secondary flex items-center justify-center gap-2 text-sm"
                >
                  <Heart size={14} /> Auto-Like
                </button>
                <button
                  onClick={handleRionaComment}
                  className="flex-1 btn-secondary flex items-center justify-center gap-2 text-sm"
                >
                  <MessageSquare size={14} /> Auto-Comment
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Output */}
        <div className="bg-white rounded-xl border border-stone-200 p-6">
          <h2 className="font-heading text-lg font-bold mb-4">Generierter Content</h2>

          {generatedContent ? (
            <div className="space-y-4">
              <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium bg-gradient-to-r ${channelColors[generatedContent.channel]} text-white`}>
                {(() => {
                  const Icon = channelIcons[generatedContent.channel] || FileText;
                  return <Icon size={14} />;
                })()}
                {channels[generatedContent.channel]?.name || generatedContent.channel}
              </div>

              <div className="p-4 bg-stone-50 rounded-lg max-h-60 overflow-y-auto">
                <p className="whitespace-pre-wrap text-sm">{generatedContent.text}</p>
              </div>

              {generatedContent.hashtags?.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {generatedContent.hashtags.map((tag, i) => (
                    <span key={i} className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">{tag}</span>
                  ))}
                </div>
              )}

              <div className="p-3 bg-amber-50 rounded-lg text-sm text-amber-800">
                <strong>CTA:</strong> {generatedContent.call_to_action}
              </div>

              {generatedContent.optimization_info && (
                <div className="p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg border border-purple-200">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-lg">✨</span>
                    <h4 className="font-medium text-purple-900">Warum dieser Post so formuliert ist</h4>
                    {generatedContent.brand_optimized && (
                      <span className="px-2 py-0.5 bg-purple-200 text-purple-800 text-xs rounded-full">Brand optimiert</span>
                    )}
                  </div>
                  <p className="text-sm text-purple-800 mb-2">{generatedContent.optimization_info.why_formatted}</p>
                  <div className="grid grid-cols-2 gap-2 text-xs text-purple-700">
                    <div><strong>Stimme:</strong> {generatedContent.optimization_info.brand_voice}</div>
                    <div><strong>Ton:</strong> {generatedContent.optimization_info.channel_tone}</div>
                    <div className="col-span-2"><strong>Hashtag-Strategie:</strong> {generatedContent.optimization_info.hashtag_strategy}</div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-48 text-stone-400">
              <Sparkles className="w-12 h-12 mb-3 opacity-50" />
              <p>Wähle einen Kanal und generiere Content</p>
            </div>
          )}
        </div>
      </div>

      {/* Recent Content */}
      <div className="bg-white rounded-xl border border-stone-200 p-6">
        <h2 className="font-heading text-lg font-bold mb-4">Letzte generierte Inhalte</h2>
        {recentContent.length === 0 ? (
          <p className="text-stone-500 text-center py-4">Noch keine Inhalte generiert</p>
        ) : (
          <div className="space-y-3">
            {recentContent.map((item) => (
              <div key={item.id} className="flex items-center justify-between p-4 bg-stone-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${channelColors[item.channel]} flex items-center justify-center text-white`}>
                    {(() => {
                      const Icon = channelIcons[item.channel] || FileText;
                      return <Icon size={16} />;
                    })()}
                  </div>
                  <div>
                    <p className="font-medium text-stone-900">{item.title}</p>
                    <p className="text-sm text-stone-500">{item.text?.substring(0, 60)}...</p>
                  </div>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  item.status === 'posted_via_riona' ? 'bg-purple-100 text-purple-700' :
                  item.status === 'draft' ? 'bg-stone-200 text-stone-600' : 'bg-green-100 text-green-700'
                }`}>
                  {item.status === 'posted_via_riona' ? '🤖 Riona' : item.status}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Riona Logs */}
      {useRiona && rionaLogs.length > 0 && (
        <div className="bg-white rounded-xl border border-stone-200 p-6" data-testid="riona-logs-section">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-lg font-bold flex items-center gap-2">
              <span>🤖</span> Riona Aktions-Log
            </h2>
            <button
              onClick={fetchData}
              className="text-sm text-purple-600 hover:text-purple-700 flex items-center gap-1"
            >
              <RefreshCw size={14} /> Aktualisieren
            </button>
          </div>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {rionaLogs.map((log) => (
              <div
                key={log.id}
                className={`p-3 rounded-lg text-sm flex items-start justify-between ${
                  log.status === 'success' ? 'bg-green-50 border border-green-200' :
                  log.status === 'simulated' ? 'bg-blue-50 border border-blue-200' :
                  log.status === 'rate_limited' ? 'bg-amber-50 border border-amber-200' :
                  'bg-red-50 border border-red-200'
                }`}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      log.mode === 'live' ? 'bg-red-200 text-red-800' : 'bg-green-200 text-green-800'
                    }`}>
                      {log.mode === 'live' ? '🔴 LIVE' : '🟢 SIM'}
                    </span>
                    <span className="font-medium text-stone-900 capitalize">{log.action_type}</span>
                    <span className={`text-xs ${
                      log.status === 'success' ? 'text-green-600' :
                      log.status === 'simulated' ? 'text-blue-600' :
                      log.status === 'rate_limited' ? 'text-amber-600' :
                      'text-red-600'
                    }`}>
                      ({log.status})
                    </span>
                  </div>
                  {log.target && <p className="text-xs text-stone-500 mt-1">Ziel: {log.target}</p>}
                  {log.error && <p className="text-xs text-red-600 mt-1">Fehler: {log.error}</p>}
                </div>
                <span className="text-xs text-stone-400">
                  {log.timestamp ? new Date(log.timestamp).toLocaleTimeString('de-DE') : ''}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SocialMediaManagerPage;
